import { Form, FormikProvider, useFormik } from "formik";
import { type ReactNode, useState } from "react";
import type { AgentSkillFlaggedFinding } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { AgentSkillKind } from "@common/types/agentSkill";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { SAVE_FAILURE_MESSAGE } from "./agentSkillCopy";
import {
    AgentSkillFormValues,
    agentSkillFormSchemaFor,
} from "./agentSkillForm";
import { AgentSkillFormFields } from "./AgentSkillFormFields";
import {
    getAgentSkillErrorFindings,
    getAgentSkillErrorMessage,
} from "./useAgentSkillLifecycle";

interface Props {
    submitLabel: string;
    initialValues: AgentSkillFormValues;
    clientName: string | null;
    kind?: AgentSkillKind;
    tools?: string[];
    initialWarnings?: string[];
    buttonSize?: "md" | "lg";
    className?: string;
    /** Places the Cancel and Save buttons; defaults to a right-aligned row. */
    renderActions?: (actions: ReactNode) => ReactNode;
    /** Resolves to the validator's warnings, or nothing when no save happened. */
    onSubmit: (values: AgentSkillFormValues) => Promise<string[] | void>;
    onClose: () => void;
}

const defaultRenderActions = (actions: ReactNode) => (
    <HStack gap={12} justify="end">
        {actions}
    </HStack>
);

export const AgentSkillEditorForm = ({
    submitLabel,
    initialValues,
    clientName,
    kind = AgentSkillKind.FIRM,
    tools = [],
    initialWarnings = [],
    buttonSize = "lg",
    className,
    renderActions = defaultRenderActions,
    onSubmit,
    onClose,
}: Props) => {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [findings, setFindings] = useState<AgentSkillFlaggedFinding[]>([]);
    const [warnings, setWarnings] = useState<string[]>(initialWarnings);
    const formik = useFormik<AgentSkillFormValues>({
        initialValues,
        validate: validateFormikWithZodSchema(agentSkillFormSchemaFor(kind)),
        onSubmit: submitHelper({
            loading: false,
            handler: async (values) => {
                setSubmitError(null);
                setFindings([]);
                const nextWarnings = await onSubmit(values);
                if (nextWarnings && nextWarnings.length > 0) {
                    setWarnings(nextWarnings);
                    return;
                }
                handleClose();
            },
            failureHandler: async (error) => {
                setFindings(getAgentSkillErrorFindings(error));
                setSubmitError(
                    getAgentSkillErrorMessage(error, SAVE_FAILURE_MESSAGE),
                );
            },
        }),
    });

    const handleClose = () => {
        formik.resetForm();
        setSubmitError(null);
        setFindings([]);
        setWarnings([]);
        onClose();
    };

    return (
        <FormikProvider value={formik}>
            <Form className={className}>
                <VStack gap={16}>
                    <AgentSkillFormFields
                        clientName={clientName}
                        kind={kind}
                        tools={tools}
                        findings={findings}
                        warnings={warnings}
                    />
                    {submitError && findings.length === 0 && (
                        <p className="text-danger">{submitError}</p>
                    )}
                </VStack>
                {renderActions(
                    <>
                        <Button
                            type="button"
                            variant="secondary"
                            size={buttonSize}
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                        >
                            {warnings.length > 0 ? "Close" : "Cancel"}
                        </Button>
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            size={buttonSize}
                            loading={formik.isSubmitting}
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{
                                description: submitLabel,
                                preventDefault: true,
                                enableOnFormTags: true,
                            }}
                        >
                            {submitLabel}
                        </ButtonWithLoader>
                    </>,
                )}
            </Form>
        </FormikProvider>
    );
};
